import { type FormEvent, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { apiPost } from "@/lib/apiClient";
import { ArrowLeft } from "lucide-react";

type FinanceCustomer = { id: string; name?: string };

export function FinanceCustomerNewPage() {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    setSaving(true);
    setError(null);
    const res = await apiPost<FinanceCustomer>("/api/finance/customers", {
      name: name.trim(),
      companyName: companyName.trim() || null,
      email: email.trim() || null,
      phone: phone.trim() || null,
    });
    setSaving(false);
    if (!res.ok || !res.data?.id) {
      setError(res.error || "Could not create customer.");
      return;
    }
    void navigate(`/finance/customers/${encodeURIComponent(res.data.id)}`);
  };

  return (
    <div className="space-y-6 p-6 sm:p-8">
      <Link
        to="/finance/customers"
        className="inline-flex items-center gap-2 text-sm font-medium text-indigo-600 hover:text-indigo-500 dark:text-indigo-300"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden />
        All customers
      </Link>

      <div>
        <h1 className="text-2xl font-bold text-slate-900 dark:text-white">New customer</h1>
        <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">Creates a row via POST /api/finance/customers.</p>
      </div>

      {error ? (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">{error}</div>
      ) : null}

      <form onSubmit={(e) => void onSubmit(e)} className="max-w-xl space-y-4 rounded-lg border border-slate-200 bg-white p-6 dark:border-white/10 dark:bg-slate-950/40">
        <label className="block text-sm">
          <span className="font-medium text-slate-700 dark:text-slate-300">Name *</span>
          <input
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 dark:border-white/20 dark:bg-slate-900"
          />
        </label>
        <label className="block text-sm">
          <span className="font-medium text-slate-700 dark:text-slate-300">Company</span>
          <input
            value={companyName}
            onChange={(e) => setCompanyName(e.target.value)}
            className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 dark:border-white/20 dark:bg-slate-900"
          />
        </label>
        <label className="block text-sm">
          <span className="font-medium text-slate-700 dark:text-slate-300">Email</span>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 dark:border-white/20 dark:bg-slate-900"
          />
        </label>
        <label className="block text-sm">
          <span className="font-medium text-slate-700 dark:text-slate-300">Phone</span>
          <input
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 dark:border-white/20 dark:bg-slate-900"
          />
        </label>
        <button
          type="submit"
          disabled={saving || !name.trim()}
          className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-500 disabled:opacity-50"
        >
          {saving ? "Creating…" : "Create customer"}
        </button>
      </form>
    </div>
  );
}
